// /**
//  * animations.js — Mavera
//  * Uses global GSAP 3, ScrollMagic, Barba, and (optionally) GSAP ScrollTrigger (all loaded via CDN in theme layout).
//  *
//  * Includes:
//  * - ScrollMagic-driven hero logo width interpolation + hide hero logo / show header logo
//  * - ScrollMagic-driven header class toggling over hero
//  * - "snap" hero-logo into header title + fade in menu/icons
//  * - Barba fade transition with proper cleanup to avoid duplicate scenes
//  * - initReveal(): ScrollTrigger-based reveal animations (.anim-fade, .anim-fade-up, .anim-fade-1, and content p)
//  *
//  * IMPORTANT:
//  * - No references to initFadeIns (removed) to avoid ReferenceError
//  * - initReveal is defined BEFORE boot so it's available on first load + Barba transitions
//  */

/* -----------------------------
    Search
----------------------------- */

let cleanupSearchOverlay = null;

function initSearchOverlay(container = document) {
  const scope = container?.querySelector ? container : document;

  const searchHolder =
    scope.querySelector("#search-holder") || document.querySelector("#search-holder");
  if (!searchHolder) return null;

  const searchInner =
    searchHolder.querySelector(".search-inner") || searchHolder;

  const searchTriggers = [
    ...scope.querySelectorAll(".js-search"),
    ...document.querySelectorAll(".js-search"),
  ].filter((el, index, arr) => arr.indexOf(el) === index);

  const header =
    document.querySelector(".js-site-header") || scope.querySelector(".js-site-header");

  const searchForm =
    searchHolder.querySelector('form[role="search"]') || searchHolder.querySelector("form");

  let isOpen = false;
  let hadSvgFillWhite = false;
  let hadTextWhite = false;

  const computedDisplay = window.getComputedStyle(searchHolder).display;
  const originalDisplay = computedDisplay === "none" ? "block" : computedDisplay;

  // initial state
  gsap.set(searchHolder, {
    autoAlpha: 0,
    display: "none",
  });

  function openSearch() {
    if (isOpen) return;
    isOpen = true;

    if (header) {
      hadSvgFillWhite = header.classList.contains("svg-fill-white");
      hadTextWhite = header.classList.contains("text-white");

      if (!hadSvgFillWhite) header.classList.add("svg-fill-white");
      if (!hadTextWhite) header.classList.add("text-white");
    }

    gsap.killTweensOf(searchHolder);
    gsap.set(searchHolder, { display: originalDisplay });
    gsap.to(searchHolder, {
      autoAlpha: 1,
      duration: 0.25,
      ease: "power1.out",
    });
  }

  function closeSearch() {
    if (!isOpen) return;
    isOpen = false;

    gsap.killTweensOf(searchHolder);
    gsap.to(searchHolder, {
      autoAlpha: 0,
      duration: 0.2,
      ease: "power1.out",
      onComplete: () => {
        gsap.set(searchHolder, { display: "none" });

        if (header) {
          if (!hadSvgFillWhite) header.classList.remove("svg-fill-white");
          if (!hadTextWhite) header.classList.remove("text-white");
        }

        hadSvgFillWhite = false;
        hadTextWhite = false;
      },
    });
  }

  function toggleSearch(e) {
    if (e) e.preventDefault();

    if (isOpen) {
      closeSearch();
    } else {
      openSearch();
    }
  }

  function handleDocumentClick(e) {
    if (!isOpen) return;

    const clickedInsideHolder = searchInner.contains(e.target);
    const clickedTrigger = e.target.closest(".js-search");

    if (!clickedInsideHolder && !clickedTrigger) {
      closeSearch();
    }
  }

  function handleFormSubmit() {
    closeSearch();
  }

  searchTriggers.forEach((trigger) => {
    trigger.addEventListener("click", toggleSearch);
  });

  document.addEventListener("click", handleDocumentClick);

  if (searchForm) {
    searchForm.addEventListener("submit", handleFormSubmit);
  }

  return () => {
    searchTriggers.forEach((trigger) => {
      trigger.removeEventListener("click", toggleSearch);
    });

    document.removeEventListener("click", handleDocumentClick);

    if (searchForm) {
      searchForm.removeEventListener("submit", handleFormSubmit);
    }

    gsap.killTweensOf(searchHolder);
    gsap.set(searchHolder, {
      autoAlpha: 0,
      display: "none",
    });

    if (header) {
      if (!hadSvgFillWhite) header.classList.remove("svg-fill-white");
      if (!hadTextWhite) header.classList.remove("text-white");
    }

    isOpen = false;
    hadSvgFillWhite = false;
    hadTextWhite = false;
  };
}

/* -----------------------------
   Reveal animations (GSAP + ScrollTrigger)
----------------------------- */
(function () {
  function initReveal(scope = document) {
    const gsap = window.gsap;
    const ST = window.ScrollTrigger;
    if (!gsap) return;

    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    const q = (sel) => scope.querySelectorAll(sel);
    const all = q('.anim-fade, .anim-fade-up, .anim-fade-1');

    if (reduce) {
      gsap.set(all, { opacity: 1, y: 0, clearProps: 'transform' });
      return;
    }

    // If ScrollTrigger missing, do NOT animate or hide
    if (!ST) {
      gsap.set(all, { opacity: 1, y: 0, clearProps: 'transform' });
      return;
    }

    try { gsap.registerPlugin(ST); } catch (e) {}

    // Kill existing triggers only in this scope (Barba-safe)
    ST.getAll().forEach(t => {
      const trig = t && t.trigger;
      if (trig && scope.contains(trig)) t.kill(true);
    });

    // --- FADE ---
    gsap.utils.toArray(q('.anim-fade')).forEach((el) => {
      gsap.from(el, {
        opacity: 0,
        duration: 1,
        ease: 'power2.out',
        overwrite: 'auto',
        scrollTrigger: {
          trigger: el,
          start: 'top 80%',
          once: true
        }
      });
    });

    // --- FADE + UP ---
    const fadeUps = gsap.utils.toArray(
      q('.anim-fade-up, .page-content p, .post-content div, .post-content h5, .post-content h3')
    );
    const parentCount = new Map();

    fadeUps.forEach((el) => {
      const p = el.parentElement || document.body;
      const i = parentCount.get(p) || 0;
      parentCount.set(p, i + 1);
      const delay = i * 0.08;

      gsap.from(el, {
        opacity: 0,
        y: 20,
        duration: 1,
        delay,
        ease: 'power2.out',
        overwrite: 'auto',
        scrollTrigger: {
          trigger: el,
          start: 'top 80%',
          once: true
        }
      });
    });

    // --- anim-fade-1 ---
    gsap.utils.toArray(q('.anim-fade-1')).forEach((el) => {
      gsap.from(el, {
        opacity: 0,
        y: 20,
        duration: 0.75,
        delay: 0.2,
        ease: 'power2.out',
        overwrite: 'auto',
        scrollTrigger: {
          trigger: el,
          once: true
        }
      });
    });

    gsap.delayedCall(0.05, () => ST.refresh());
  }

  window.initReveal = initReveal;
})();

/* -----------------------------
   Helpers
----------------------------- */
function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }
function getIsMobileViewport() {
  return window.matchMedia('(max-width: 1023px)').matches;
}
function pxToNumber(px) {
  const n = parseFloat(String(px).replace('px', ''));
  return Number.isFinite(n) ? n : 0;
}
function setOpacity(el, v) {
  if (!el) return;
  el.style.opacity = String(v);
}

/* -----------------------------
   Sync classes from Barba container → body/header/footer
   IMPORTANT: we also clear "animated state" classes so they don't stick
----------------------------- */
function applyPageClasses(container) {
  if (!container) return;

  const body = document.body;
  const header = document.querySelector('.js-site-header');
  const footer = document.querySelector('.js-site-footer');

  const nextBody = (container.getAttribute('data-body-class') || '')
    .trim()
    .replace(/\s+/g, ' ');

  const nextHeader = (container.getAttribute('data-header-class') || '')
    .trim()
    .replace(/\s+/g, ' ');

  const nextFooter = (container.getAttribute('data-footer-class') || '')
    .trim()
    .replace(/\s+/g, ' ');

  const BODY_MANAGED = [
    'bg-secondary', 'bg-white', 'bg-primary',
    'text-primary', 'text-white'
  ];

  const HEADER_MANAGED = [
    'invisible', 'text-white', 'text-primary', 'svg-fill-white', 'svg-fill-primary',
    'bg-transparent', 'bg-primary', 'svg-primary',
    'svg-fill-black', 'text-black'
  ];

  const FOOTER_MANAGED = [
    'border-white', '!mt-0', 'mt-50'
  ];

  BODY_MANAGED.forEach(c => body.classList.remove(c));
  HEADER_MANAGED.forEach(c => header && header.classList.remove(c));
  FOOTER_MANAGED.forEach(c => footer && footer.classList.remove(c));

  nextBody.split(' ').filter(Boolean).forEach(c => body.classList.add(c));
  nextHeader.split(' ').filter(Boolean).forEach(c => header && header.classList.add(c));
  nextFooter.split(' ').filter(Boolean).forEach(c => footer && footer.classList.add(c));

  const mount = document.querySelector('.js-collection-footer');
  if (mount) {
    const show = (container.getAttribute('data-show-collection-footer') || '').trim() === '1';

    if (show) {
      const source = container.querySelector('.js-collection-footer-source');
      if (source) {
        mount.innerHTML = source.innerHTML;
        mount.classList.remove('hidden');
      } else {
        mount.classList.add('hidden');
        mount.innerHTML = '';
      }
    } else {
      mount.classList.add('hidden');
      mount.innerHTML = '';
    }
  }
}

/* -----------------------------
   Main module (ScrollMagic + GSAP)
----------------------------- */
const MaveraAnimations = (() => {
  let controller = null;
  let scenes = [];
  let resizeHandler = null;
  let snapPlayed = false;

  function destroy() {
    scenes.forEach(s => { try { s.destroy(true); } catch (e) {} });
    scenes = [];

    if (controller) {
      try { controller.destroy(true); } catch (e) {}
      controller = null;
    }

    if (resizeHandler) {
      window.removeEventListener('resize', resizeHandler);
      resizeHandler = null;
    }

    snapPlayed = false;
  }

  function init(root = document) {
    destroy();

    // reveal (safe even if ST isn't present)
    window.initReveal && window.initReveal(root);

    const hasScrollMagic =
      !!window.ScrollMagic && typeof window.ScrollMagic.Controller === 'function';
    if (!hasScrollMagic) return;

    controller = new ScrollMagic.Controller();

    const hero = root.querySelector('.js-hero') || root.querySelector('.hero');
    const header = document.querySelector('.js-site-header');

    const heroLogoWidthEl = root.querySelector('.hero__logo');
    const ribbonEl = root.querySelector('.js-ribbon') || document.querySelector('.js-ribbon');
    const heroLogoSnapEl = root.querySelector('.hero-logo');

    const headerLogo = document.querySelector('.header__logo');
    const headerTitle = document.querySelector('.header__title');
    const headerMenu = document.querySelector('.header__menu');
    const headerIcons = document.querySelector('.header__icons');

    if (!hero && !heroLogoWidthEl && !heroLogoSnapEl) return;

    /* -----------------------------
       1) Logo width interpolation + ribbon fade + show header on end
    ----------------------------- */
    if (heroLogoWidthEl && window.gsap) {
      const applyWidthState = () => {
        const isMobile = getIsMobileViewport();
        const logoStartWidth = isMobile ? '300px' : '450px';
        const logoEndWidth = isMobile ? '100px' : '170px';

        window.gsap.set(heroLogoWidthEl, { width: logoStartWidth });
        heroLogoWidthEl.__mwStart = pxToNumber(logoStartWidth);
        heroLogoWidthEl.__mwEnd = pxToNumber(logoEndWidth);
      };

      applyWidthState();

      heroLogoWidthEl.classList.remove('invisible');
      window.gsap.killTweensOf(heroLogoWidthEl);
      window.gsap.fromTo(
        heroLogoWidthEl,
        { opacity: 0 },
        {
          opacity: 1,
          duration: 1.5,
          ease: 'power1.out',
          overwrite: 'auto',
          onComplete: () => window.gsap.set(heroLogoWidthEl, { clearProps: 'opacity' })
        }
      );

      let ribbonIntroDone = false;
      if (ribbonEl) {
        window.gsap.killTweensOf(ribbonEl);
        window.gsap.set(ribbonEl, { opacity: 0 });
        window.gsap.to(ribbonEl, {
          opacity: 1,
          duration: 1.5,
          delay: .75,
          ease: 'power1.out',
          overwrite: 'auto',
          onComplete: () => { ribbonIntroDone = true; }
        });
      }

      const buildLogoWidthScene = () => {
        scenes = scenes.filter(s => s !== heroLogoWidthEl.__mwScene);
        if (heroLogoWidthEl.__mwScene) {
          try { heroLogoWidthEl.__mwScene.destroy(true); } catch (e) {}
          heroLogoWidthEl.__mwScene = null;
        }

        const duration = Math.max(1, Math.round(window.innerHeight / 2));

        const scene = new ScrollMagic.Scene({
          triggerElement: 'body',
          triggerHook: 0,
          offset: -16,
          reverse: false,
          duration,
          onComplete: () => {}
        })
          .on('progress', (e) => {
            const p = clamp(e.progress, 0, 1);

            const start = heroLogoWidthEl.__mwStart ?? 0;
            const end = heroLogoWidthEl.__mwEnd ?? 0;
            const w = start + (end - start) * p;

            window.gsap.set(heroLogoWidthEl, { width: `${w}px` });

            if (ribbonEl && ribbonIntroDone) {
              window.gsap.set(ribbonEl, { opacity: 1 - p });
            }
          })
          .on('leave', (e) => {
            if (!header) return;
            if (!e || e.scrollDirection === 'FORWARD') {
              heroLogoWidthEl.classList.add('invisible');
              header.classList.remove('invisible');
              if (headerLogo) headerLogo.classList.remove('invisible');
            }
          })
          .on('end', () => {
            const header = document.querySelector('.js-site-header');
            if (header) header.style.opacity = '1';
          })
          .addTo(controller);

        heroLogoWidthEl.__mwScene = scene;
        scenes.push(scene);
      };

      buildLogoWidthScene();

      resizeHandler = () => {
        applyWidthState();
        buildLogoWidthScene();
        try { controller.update(true); } catch (e) {}
      };
      window.addEventListener('resize', resizeHandler);
    }

    /* -----------------------------
       2) Header class toggling over hero
    ----------------------------- */
    if (header) header.classList.add('is-top');

    if (hero && header) {
      const scene = new ScrollMagic.Scene({
        triggerElement: hero,
        triggerHook: 0,
        offset: -20,
        duration: hero.offsetHeight
      })
        .on('enter', () => {
          header.classList.add('bg-transparent');
          header.classList.add('text-white');
          header.classList.remove('bg-primary');
          header.classList.remove('text-primary');
          header.classList.remove('svg-fill-primary');
        })
        .on('leave', () => {
          header.classList.remove('bg-transparent');
          header.classList.add('bg-primary');
          header.classList.add('text-primary');
          header.classList.add('svg-primary');
        })
        .addTo(controller);

      scenes.push(scene);
    }

    /* -----------------------------
       3) Snap hero-logo into header title + show menu/icons
    ----------------------------- */
    (function initHeroSnap() {
      if (!hero || !heroLogoSnapEl || !header || !headerTitle || !window.gsap) return;

      setOpacity(headerMenu, 0);
      setOpacity(headerIcons, 0);

      window.gsap.killTweensOf(heroLogoSnapEl);
      if (headerMenu) window.gsap.killTweensOf(headerMenu);
      if (headerIcons) window.gsap.killTweensOf(headerIcons);

      function createTween() {
        const logoRect = heroLogoSnapEl.getBoundingClientRect();
        const targetRect = headerTitle.getBoundingClientRect();

        const dx = (targetRect.left + targetRect.width / 2) - (logoRect.left + logoRect.width / 2);
        const dy = (targetRect.top + targetRect.height / 2) - (logoRect.top + logoRect.height / 2);
        const scale = (targetRect.height / logoRect.height) * 0.9;

        return window.gsap.timeline()
          .to(heroLogoSnapEl, { duration: 0.6, x: dx, y: dy, scale, ease: 'power2.inOut', overwrite: 'auto' })
          .to([headerMenu, headerIcons].filter(Boolean), { duration: 0.4, opacity: 1, ease: 'power1.out' }, '-=0.3')
          .set(heroLogoSnapEl, { clearProps: 'all' });
      }

      const scene = new ScrollMagic.Scene({
        triggerElement: hero,
        triggerHook: 0,
        duration: hero.offsetHeight
      })
        .on('enter', () => {
          if (snapPlayed) return;
          createTween();
          snapPlayed = true;
        })
        .addTo(controller);

      scenes.push(scene);
    })();
  }

  return { init, destroy };
})();

/* -----------------------------
   Barba bypass
----------------------------- */
function shouldBypassBarba(el) {
  const link = el && el.closest ? el.closest('a') : null;
  if (!link) return false;

  const href = link.getAttribute('href') || '';
  if (!href) return false;

  if (link.classList.contains('js-open-cart')) return true;

  if (link.classList.contains('external-link')) return true;

  if (
    href === '/account' ||
    href.startsWith('/account/') ||
    href.startsWith('/checkout') ||
    href.startsWith('/apps/')
  ) return true;

  if (link.target === '_blank' || link.rel?.includes('external')) return true;

  if (href.startsWith('#')) return true;

  try {
    const url = new URL(href, window.location.origin);
    if (url.origin !== window.location.origin) return true;
  } catch (e) {}

  return false;
}

/* -----------------------------
   Barba init (once)
----------------------------- */
function initBarbaOnce() {
  if (!window.barba || window.__maveraBarbaInited) return;
  window.__maveraBarbaInited = true;

  window.barba.init({
    prevent: ({ el }) => shouldBypassBarba(el),

    transitions: [{
      name: 'fade',

      beforeLeave() {
        MaveraAnimations.destroy();

        if (cleanupSearchOverlay) {
          cleanupSearchOverlay();
          cleanupSearchOverlay = null;
        }
      },

      beforeEnter(data) {
        applyPageClasses(data.next.container);
        window.scrollTo(0, 0);
        cleanupSearchOverlay = initSearchOverlay(data.next.container);
      },

      leave(data) {
        return gsap.to(data.current.container, {
          opacity: 0,
          filter: 'blur(8px)',
          duration: 0.35,
          ease: 'power2.out',
          clearProps: 'filter'
        });
      },

      enter(data) {
        return gsap.from(data.next.container, {
          opacity: 0,
          filter: 'blur(8px)',
          duration: 0.35,
          ease: 'power2.out',
          clearProps: 'filter'
        });
      },

      afterEnter(data) {
        MaveraAnimations.init(data.next.container);
        window.initReveal && window.initReveal(data.next.container);

        if (window.MobileNav && typeof window.MobileNav.close === 'function') {
          window.MobileNav.close();
        }

        window.__navHoldOpen = false;
      }
    }]
  });
}

/* -----------------------------
   Boot
----------------------------- */
function boot() {
  const container = document.querySelector('[data-barba="container"]');

  applyPageClasses(container);

  MaveraAnimations.init(document);

  cleanupSearchOverlay = initSearchOverlay(document);

  initBarbaOnce();

  window.initReveal && window.initReveal(document);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

(function () {
  if (window.__maveraProductUIInited) return;
  window.__maveraProductUIInited = true;

  const gsap = window.gsap;

  function getContentEl(toggle) {
    const parent = toggle.parentElement;
    return parent ? parent.nextElementSibling : null;
  }

  function setIcons(toggle, open) {
    const plus = toggle.querySelector('.icon-plus');
    const minus = toggle.querySelector('.icon-minus');
    if (plus) plus.classList.toggle('hidden', open);
    if (minus) minus.classList.toggle('hidden', !open);
    toggle.setAttribute('aria-expanded', String(open));
  }

  function openAccordion(content, toggle) {
    content.classList.remove('hidden');
    content.classList.add('block');

    const targetH = content.scrollHeight;

    if (gsap) {
      gsap.killTweensOf(content);
      gsap.fromTo(
        content,
        { height: 0, opacity: 0 },
        {
          height: targetH,
          opacity: 1,
          duration: 0.35,
          ease: 'power2.out',
          overwrite: 'auto',
          onComplete: () => {
            content.style.height = 'auto';
            content.style.opacity = '';
          }
        }
      );
    } else {
      content.style.height = '';
      content.style.opacity = '';
    }

    setIcons(toggle, true);
  }

  function closeAccordion(content, toggle) {
    const startH = content.scrollHeight;

    if (gsap) {
      gsap.killTweensOf(content);
      gsap.fromTo(
        content,
        { height: startH, opacity: 1 },
        {
          opacity: 0,
          duration: 0.125,
          ease: 'power2.inOut',
          overwrite: 'auto',
          onComplete: () => {
            content.classList.add('hidden');
            content.classList.remove('block');
            content.style.opacity = '';
          }
        }
      );
    } else {
      content.classList.add('hidden');
      content.classList.remove('block');
    }

    setIcons(toggle, false);
  }

  // --- 1) Accordion: delegated click (works after Barba nav) ---
  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-faq-toggle]');
    if (!toggle) return;

    const section = toggle.closest('#product-accordion');
    if (!section) return;

    const content = getContentEl(toggle);
    if (!content) return;

    const isOpen = content.classList.contains('block') && !content.classList.contains('hidden');

    if (isOpen) closeAccordion(content, toggle);
    else openAccordion(content, toggle);
  });

  // --- 2) Qty +/-: delegated click (works after Barba nav) ---
  document.addEventListener('click', (e) => {
    const minus = e.target.closest('.js-qty-minus');
    const plus  = e.target.closest('.js-qty-plus');
    if (!minus && !plus) return;

    const wrapper = e.target.closest('.inline-flex');
    if (!wrapper) return;

    const qtyValue = wrapper.querySelector('.js-qty-value');
    const form = wrapper.closest('form');
    if (!qtyValue || !form) return;

    const input = form.querySelector('.js-qty-input');
    const addBtn = wrapper.querySelector('.js-add-to-cart');

    let value = parseInt(qtyValue.textContent, 10) || 1;
    const max = addBtn ? (parseInt(addBtn.dataset.available, 10) || Infinity) : Infinity;

    if (minus) value = Math.max(1, value - 1);
    if (plus)  value = Math.min(max, value + 1);

    qtyValue.textContent = value;
    if (input) input.value = value;
  });
})();